import React from 'react';
import styled from '@emotion/styled';
import { gradientText } from '../styles/mixins';

const HeaderContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: ${props => props.compact ? '60px' : '80px'};
  background: ${props => props.transparent ? 'transparent' : 'linear-gradient(180deg, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.6) 100%)'};
  backdrop-filter: ${props => props.transparent ? 'none' : 'blur(20px)'};
  border-bottom: ${props => props.transparent ? 'none' : '1px solid rgba(255, 255, 255, 0.1)'};
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 30px;
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const KuutioVideo = styled.video`
  height: ${props => props.size || '50px'};
  width: auto;
  opacity: ${props => props.opacity || 0.8};
  transition: opacity 0.3s ease;
  
  &:hover {
    opacity: 1;
  }
`;

const TitleSection = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
`;

const Title = styled.h1`
  margin: 0;
  font-size: ${props => props.compact ? '1.2rem' : '1.8rem'};
  font-weight: 700;
  ${gradientText('#4CAF50', '#2196F3', '#4CAF50')}
  cursor: ${props => props.clickable ? 'pointer' : 'default'};
  transition: all 0.2s ease;
  position: relative;
  
  ${props => props.clickable && `
    &:hover {
      transform: scale(1.02);
    }
    
    &:after {
      content: '▼';
      margin-left: 8px;
      font-size: 0.8em;
      opacity: 0.6;
    }
  `}
`;

const Subtitle = styled.div`
  color: rgba(255, 255, 255, 0.7);
  font-size: ${props => props.compact ? '0.8rem' : '0.9rem'};
  margin-left: 20px;
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
`;


/**
 * Reusable header component with rotating Kuutio logo for all screens
 * 
 * @param {string} title - Main title text
 * @param {string} subtitle - Optional subtitle text
 * @param {boolean} compact - Use smaller, compact version (default: true)
 * @param {boolean} transparent - Use transparent background
 * @param {string} kuutioSize - Size of the Kuutio video (default: 40px)
 * @param {number} kuutioOpacity - Opacity of the Kuutio video (default: 0.8)
 * @param {function} onTitleClick - Callback when title is clicked (makes title clickable)
 * @param {React.ReactNode} rightContent - Custom content for right section
 * @param {React.ReactNode} leftContent - Custom content for left section (after Kuutio)
 */
const ScreenHeader = ({
  title,
  subtitle,
  compact = true, // Now compact by default
  transparent = false,
  kuutioSize = '40px', // Smaller default size for compact
  kuutioOpacity = 0.8,
  rightContent,
  leftContent,
  onTitleClick, // New prop for clickable title
  ...props
}) => {
  return (
    <HeaderContainer compact={compact} transparent={transparent} {...props}>
      <LeftSection>
        <KuutioVideo
          src="/assets/kuutio.mp4"
          autoPlay
          loop
          muted
          playsInline
          size={kuutioSize}
          opacity={kuutioOpacity}
        />
        {(title || subtitle) && (
          <TitleSection>
            {title && (
              typeof title === 'string' ? (
                <Title 
                  compact={compact} 
                  clickable={!!onTitleClick}
                  onClick={onTitleClick}
                >
                  {title}
                </Title>
              ) : (
                // For React components (like ScreenNavigation), render directly
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  {title}
                </div>
              )
            )}
            {subtitle && <Subtitle compact={compact}>{subtitle}</Subtitle>}
          </TitleSection>
        )}
        {leftContent}
      </LeftSection>

      <RightSection>
        {rightContent}
      </RightSection>
    </HeaderContainer>
  );
};

export default ScreenHeader;